import type { Pinia } from 'pinia';
import type { Router } from 'vue-router';
import type { ErrorReport } from '~/lib/sentry';

// Sentry used to be in the entry chunk (~60KB of every page's JS). It now loads once the page is idle;
// errors raised before then are buffered and sent once it has loaded.
// (Replaces sentry.client.config.ts: without that file @sentry/nuxt adds no client plugin of its own.)
export default defineNuxtPlugin({
    name: 'sentry-lazy',
    enforce: 'pre',
    setup(nuxtApp) {
        const { dsn, tracesSampleRate } = useRuntimeConfig().public.sentry as {
            dsn?: string;
            tracesSampleRate: number;
        };

        // from the inline script in nuxt.config's app.head
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const early = (window as { __earlyErrors?: { errors: unknown[]; on: EventListener } }).__earlyErrors;

        if (early) {
            removeEventListener('error', early.on);
            removeEventListener('unhandledrejection', early.on);
        }

        if (import.meta.dev || !dsn) {
            return;
        }

        const buffer: ErrorReport[] = (early?.errors ?? []).map(error => ({ error, source: 'global' }));

        let send = (report: ErrorReport): void => {
            buffer.push(report);
        };

        const onError = (event: ErrorEvent): void => send({ error: event.error ?? event.message, source: 'global' });
        const onRejection = (event: PromiseRejectionEvent): void => send({ error: event.reason, source: 'global' });

        addEventListener('error', onError);
        addEventListener('unhandledrejection', onRejection);

        nuxtApp.hook('vue:error', (error, _instance, info) => send({ error, source: 'vue', info }));
        nuxtApp.hook('app:error', error => {
            const statusCode = (error as { statusCode?: number }).statusCode;

            // redirects and client errors aren't bugs
            if (statusCode && statusCode >= 300 && statusCode < 500) {
                return;
            }

            send({ error, source: 'app' });
        });

        onNuxtReady(async () => {
            const sentry = await import('~/lib/sentry');

            sentry.initSentry({
                dsn,
                tracesSampleRate,
                app: nuxtApp.vueApp,
                router: nuxtApp.$router as Router,
                pinia: nuxtApp.$pinia as Pinia
            });

            // Sentry's own global handlers take over from here
            removeEventListener('error', onError);
            removeEventListener('unhandledrejection', onRejection);

            send = sentry.report;
            buffer.splice(0).forEach(sentry.report);
        });
    }
});
